import { useState } from 'react';
import type { PaymentException } from '@gatecheckpro/contracts';
import {
  Button,
  Card,
  EmptyState,
  LoadingRegion,
  Notice,
  formatMoney,
} from '@gatecheckpro/design-system';
import { api } from '../lib/api.ts';
import { useResource } from '../lib/useResource.ts';
import { FailureNotice } from '../lib/refusal.tsx';

/**
 * Payments GateCheckPro could not resolve on its own (ADR 0023).
 *
 * Note what this page cannot do. There is no "mark as paid", no "issue tickets
 * anyway", and no free-text amount an operator can enter. That is not an
 * oversight to be filled in later — a button that issues tickets on a human's
 * say-so is the single most abusable control a ticketing platform can have. It
 * is the one an insider reaches for, the one a compromised operator account is
 * worth having, and the one nobody can audit afterwards because there is no
 * external evidence to compare against.
 *
 * What an operator *can* do is order a re-verification, which asks the provider
 * again and lets the ordinary verified path decide, or record that a case was
 * settled outside the system. Neither manufactures payment authority.
 */
const RESOLUTIONS = [
  {
    value: 'RESOLVED_BY_REVERIFICATION',
    label: 'Ask the provider again',
    explanation:
      'GateCheckPro re-queries the provider and re-runs the same checks. If the payment now verifies, tickets are issued by the ordinary path — not by this decision.',
  },
  {
    value: 'RESOLVED_EXTERNALLY',
    label: 'Settled outside GateCheckPro',
    explanation:
      'Records that this was handled elsewhere — a refund, a bank reversal, a conversation with the provider. Issues nothing and changes no balance.',
  },
  {
    value: 'NO_ACTION_REQUIRED',
    label: 'No action needed',
    explanation: 'Records a judgement that nothing is wrong. Issues nothing.',
  },
] as const;

const KIND_MEANINGS: Readonly<Record<PaymentException['kind'], string>> = {
  AMOUNT_MISMATCH:
    'The provider reported a successful payment for an amount that is not what this order owes.',
  CURRENCY_MISMATCH: 'The provider reported a payment in a different currency to the order.',
  REFERENCE_MISMATCH: 'The provider reported a success against a reference we did not send.',
  CUSTOMER_MISMATCH: 'The person who paid does not match the person who placed the order.',
  PAID_AFTER_INVENTORY_LOST:
    'The payment verified, but this order’s reservation had already lapsed and the tickets went back on sale. Money moved and inventory is gone.',
  ISSUANCE_FAILED: 'The payment verified but tickets could not be written.',
  UNRECOGNISED_TRANSACTION: 'The provider reported something GateCheckPro could not match.',
};

export function PaymentExceptionsPage() {
  const { resource, reload } = useResource<{ exceptions: PaymentException[] }>(
    '/api/v1/ops/payment-exceptions',
  );
  const [open, setOpen] = useState<string | null>(null);
  const [resolution, setResolution] = useState<string>(RESOLUTIONS[0].value);
  const [note, setNote] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function submit(formEvent: React.FormEvent, exceptionId: string): Promise<void> {
    formEvent.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.request(`/api/v1/ops/payment-exceptions/${exceptionId}/resolve`, {
        method: 'POST',
        body: { resolution, note },
      });
      setOpen(null);
      setNote('');
      reload();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="gc-page-header">
        <span className="gc-eyebrow">Ticketing support</span>
        <h1>Payment exceptions</h1>
        <p className="gc-muted">
          Payments where what the provider said and what the order says do not agree. Nothing here
          has issued a ticket, and nothing on this page can issue one directly.
        </p>
      </div>

      <FailureNotice error={error} />

      {resource.status === 'loading' && <LoadingRegion label="Loading payment exceptions" />}
      {resource.status === 'failed' && <FailureNotice error={resource.error} />}

      {resource.status === 'loaded' &&
        (resource.data.exceptions.length === 0 ? (
          <EmptyState title="Nothing needs a human">
            Every payment GateCheckPro has seen either verified against the order or failed cleanly.
            Exceptions appear here the moment one does not.
          </EmptyState>
        ) : (
          <div className="gc-stack">
            {resource.data.exceptions.map((exception) => (
              <Card
                key={exception.paymentExceptionId}
                title={exception.orderNumber}
                eyebrow={exception.organizationName}
                action={
                  <Button
                    onClick={() =>
                      setOpen((current) =>
                        current === exception.paymentExceptionId
                          ? null
                          : exception.paymentExceptionId,
                      )
                    }
                  >
                    {open === exception.paymentExceptionId ? 'Cancel' : 'Resolve'}
                  </Button>
                }
              >
                <div className="gc-stack gc-stack--tight">
                  <Notice tone="caution" title={exception.kind.replaceAll('_', ' ').toLowerCase()}>
                    {KIND_MEANINGS[exception.kind]}
                  </Notice>

                  <div className="gc-row gc-row--between">
                    <span>Order expects</span>
                    <span className="gc-metric__value">
                      {formatMoney(exception.expectedAmount)}
                    </span>
                  </div>
                  <div className="gc-row gc-row--between">
                    <span>Opened</span>
                    <span className="gc-muted">
                      {new Date(exception.openedAt).toLocaleString()}
                    </span>
                  </div>

                  {/*
                    What we expected against what we observed, so nobody has to
                    reconstruct it from logs at two in the morning.
                  */}
                  <details>
                    <summary>What did not match</summary>
                    <dl className="gc-stack gc-stack--tight">
                      {Object.entries(exception.detail).map(([key, value]) => (
                        <div key={key} className="gc-row gc-row--between">
                          <dt className="gc-muted">{key}</dt>
                          <dd className="gc-mono">{String(value)}</dd>
                        </div>
                      ))}
                    </dl>
                  </details>

                  {open === exception.paymentExceptionId && (
                    <form
                      className="gc-stack"
                      onSubmit={(formEvent) => void submit(formEvent, exception.paymentExceptionId)}
                    >
                      <div className="gc-field">
                        <label className="gc-field__label" htmlFor="resolution">
                          What are you doing about it?
                        </label>
                        <select
                          id="resolution"
                          className="gc-select"
                          value={resolution}
                          onChange={(changeEvent) => setResolution(changeEvent.target.value)}
                        >
                          {RESOLUTIONS.map((entry) => (
                            <option key={entry.value} value={entry.value}>
                              {entry.label}
                            </option>
                          ))}
                        </select>
                        <span className="gc-field__hint">
                          {RESOLUTIONS.find((entry) => entry.value === resolution)?.explanation}
                        </span>
                      </div>

                      <div className="gc-field">
                        <label className="gc-field__label" htmlFor="note">
                          Why
                        </label>
                        <textarea
                          id="note"
                          className="gc-input"
                          rows={3}
                          value={note}
                          minLength={3}
                          required
                          onChange={(changeEvent) => setNote(changeEvent.target.value)}
                        />
                        <span className="gc-field__hint">
                          Recorded against your name in the audit trail.
                        </span>
                      </div>

                      <Button type="submit" variant="primary" busy={busy}>
                        Record decision
                      </Button>
                    </form>
                  )}
                </div>
              </Card>
            ))}
          </div>
        ))}
    </>
  );
}
